import EventEmitter from "events"
import Cron from "./Cron"

type TGetter<T> = {
    success:(cache:T) => void,
    fail:(e:any) => void
}

class Cache<VALUE> {
    value:VALUE
    expire:number
    constructor(data:VALUE,expire:number){
        this.value = data
        this.expire = expire
    }
    get expired(){return this.expire && Date.now()>this.expire}
}

export default abstract class CacheMan<KEY,VALUE> extends EventEmitter{
    private _cache:Record<string, Cache<VALUE>> = {}
    private _getters : Record<string, TGetter<VALUE>[]> = {}
    private _duration:number
    private _name: string

    abstract fetch(key:KEY) : Promise<VALUE>
    abstract fetchBatch(keys:KEY[]) : Promise<VALUE[]>

    serialize(key:KEY,val:VALUE) : any { return val }
    deserialize(key:KEY,val:string) : VALUE { return val as VALUE }

    serializeKey(key:KEY) { return key as string }
    deserializeKey(key:string) { return key as KEY }
    has(key:KEY) { return !!this.fromCache(key) }
    get name(){return this._name}

    protected get cache(){return this._cache}

    constructor(name:string, durationInSeconds:number){
        super()
        this._name = name
        this._duration = durationInSeconds
        if (durationInSeconds) Cron.i.start(name, this.collect.bind(this), 60000)
    }

    fromCache(key:KEY, ignoreExpired:boolean=false, extendCache:boolean=false) {
        const data = this._cache[this.serializeKey(key)]
        if (data && (ignoreExpired || !data.expired)) {
            if (extendCache) data.expire = Date.now() + this._duration*1000
            return data.value
        }
        else return null
    }

    get(key:KEY) : Promise<VALUE>{
        return new Promise(async (success,fail) => {
            const cache = this.fromCache(key)
            if (cache) return success(cache)
            
            const strKey = this.serializeKey(key)
            if (strKey in this._getters) return this._getters[strKey].push({success,fail})
            else this._getters[strKey] = [{success,fail}]

            try {
                const ret = await this.fetch(key)
                this._set(key, ret)
                this._resolve(strKey, ret)
            }catch(e){
                this._reject(strKey, e)
            }
        })
    }

    async getBatch(keys:KEY[]) : Promise<VALUE[]> {
        const fetchIds:KEY[] = []
        const pendings:Promise<VALUE>[] = []
        for(const key of keys) {
            const cache = this.fromCache(key)
            const strKey = this.serializeKey(key)
            if (cache) {}
            else {
                if (strKey in this._getters) {}
                else fetchIds.push(key)
                pendings.push(this._wait(key))
            }
        }

        if (fetchIds.length) {
            const missing = await this.fetchBatch(fetchIds)
            if (fetchIds.length!=missing.length) throw {status:400,msg:`${this.name}::fetchBatch returned incorrect # of recrods. expected ${fetchIds.length} but got ${missing.length}`}
            for(let i=0; i<missing.length; i++) {
                const key = fetchIds[i]
                this._set(key, missing[i])
                this._resolve(this.serializeKey(key), missing[i])
            }
        }
        await Promise.all(pendings)
        return keys.map(key => this.fromCache(key, true)!)
    }

    private _wait(key:KEY) : Promise<VALUE> {
        const strKey = this.serializeKey(key)
        return new Promise((success,fail) => {
            if (this._getters[strKey]) this._getters[strKey].push({success,fail})
            else this._getters[strKey] = [{success,fail}]
        })
    }

    private _resolve(strKey:string, value:VALUE) {
        const getters = this._getters[strKey]
        delete this._getters[strKey]
        for(const cb of getters) {
            cb.success(value)
        }
    }
    
    private _reject(strKey:string, e:any){
        const getters = this._getters[strKey]
        delete this._getters[strKey]
        for(const cb of getters) {
            cb.fail(e)
        }
    }

    add(key:KEY, value:VALUE) {
        return this._set(key, value)
    }
    
    remove(key:KEY){
        const cache = this.fromCache(key, true)
        if (cache) {
            delete this._cache[this.serializeKey(key)]
            this.onRemove(key, cache)
        }
    }

    expire(key:KEY){
        delete this._cache[this.serializeKey(key)]
        return this
    }

    protected _set(key:KEY, value:VALUE) {
        const cache = new Cache(value, this._duration ? this._duration*1000 + Date.now() : 0)
        this._cache[this.serializeKey(key)] = cache
        this.onUpdate(key, value)
        return value
    }

    protected onUpdate(key:KEY, value:VALUE) {
        this.emit("update", key, value)
    }
    protected onRemove(key:KEY, value:VALUE) {
        this.emit("remove", key, value)
    }
    protected onCollect(key:KEY, value:VALUE) {
        this.emit("collect", key, value)
    }

    private collect(next:Function){
        const expired:string[] = []
        for(const keyval of Object.entries(this._cache)) {
            if (keyval[1].expired) expired.push(keyval[0])
        }
        for(const key of expired){
            const cache = this._cache[key]
            delete this._cache[key]
            this.onCollect(this.deserializeKey(key), cache.value)
        }
    }
}